import { Link } from "react-router-dom";
import {
  Dices, Swords, Users, MapPinned, ScrollText, Sparkles, Camera, Wand2, BookOpen,
  Video, Mic, Radio, Smartphone, Eye, Flame, DoorOpen, Target, Bot, PartyPopper,
  Puzzle, Trophy, Newspaper, Rss, UserPlus, Award, Store, Coins, Skull, Compass,
  Crown, Layers, Footprints, History,
} from "lucide-react";

const scrollToCluster = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const RAID_MODES = [
  { name: "Alliance", color: "#3B82F6", icon: Compass, desc: "Many small tables converge into one shared story." },
  { name: "Tournament", color: "#D4AF37", icon: Trophy, desc: "Bracketed, head-to-head competition between tables." },
  { name: "Party", color: "#22C55E", icon: Users, desc: "One shared goal. One massive co-op table." },
  { name: "BBEBBEG", color: "#D90429", icon: Skull, desc: "An entire raid vs. one colossal boss." },
  { name: "Dungeon Crawl", color: "#F59E0B", icon: Footprints, desc: "Race for treasure, survivors, or the clock." },
];

const CLUSTERS = [
  {
    id: "rules-engine",
    tag: "Character & Rules",
    name: "Zero Math. Infinite Customization.",
    accent: "#D4AF37",
    bg: "dark",
    icon: ScrollText,
    cards: [
      { icon: ScrollText, title: "Full PF2e Rules Engine", desc: "Preloaded SRD ancestries, classes, feats, spells & items — AC, HP, and DCs auto-calculate as you play.", hero: true },
      { icon: Layers, title: "Growing D&D 5e Support", desc: "Races, classes, spells and attacks — 5e tables are welcome at the table too." },
      { icon: Wand2, title: "Step-by-Step Ability Wizards", desc: "PF2e Ability Boosts and 5e Point Buy / Standard Array / Roll, guided start to finish." },
      { icon: Camera, title: "Photo-to-Sheet Import", desc: "Snap a photo of a paper character sheet and watch it become a full digital sheet." },
      { icon: Dices, title: "Two Sheet Modes", desc: "Full digital sheet, or Paper Character mode with a giant tappable dice pad." },
      { icon: Swords, title: "GM Override, Always", desc: "Automate the math, but the GM can override any rule with a single tap." },
    ],
  },
  {
    id: "maps-tokens",
    tag: "Maps & Token Magic",
    name: "Living Canvases That React To Every Move.",
    accent: "#3B82F6",
    bg: "wood",
    icon: MapPinned,
    cards: [
      { icon: Camera, title: "Live Camera-as-Token", desc: "Your actual webcam feed becomes your token on the map — no static art required.", hero: true },
      { icon: Sparkles, title: "Token Animation Maker", desc: "Build custom animated tokens instead of settling for a still image." },
      { icon: DoorOpen, title: "Doors, Locks & Puzzles", desc: "Lockable, pickable, breakable doors — plus riddle and item puzzle locks on doors and chests." },
      { icon: Eye, title: "Fog of War & Dynamic Lighting", desc: "3-mode darkness system with carryable torches that actually flicker." },
      { icon: Target, title: "NPC Sight Cones", desc: "Cones that sweep, pivot, and roll a real Stealth check the moment you're spotted." },
      { icon: Puzzle, title: "AOE Templates & Traps", desc: "Cone, Line and Circle blasts with configurable saves and on-hit effects." },
      { icon: Bot, title: "Combat Tracker + Auto-Attack AI", desc: "NPCs can fight themselves — or gang up and ambush the party as a group." },
    ],
  },
  {
    id: "voice-table",
    tag: "Voice, Video & Table Feel",
    name: "Built for Phones & Physical Tabletop Play.",
    accent: "#3B82F6",
    bg: "dark",
    icon: Mic,
    cards: [
      { icon: Video, title: "Built-in Voice & Video", desc: "Whole-table calling baked right in — no third-party app to juggle.", hero: true },
      { icon: Radio, title: "GM Whisper Voice", desc: "Pull one player aside for a private word without ever leaving the call." },
      { icon: Mic, title: "Push-to-Talk", desc: "Optional hold-to-talk for quieter tables and noisier living rooms." },
      { icon: Smartphone, title: "Mobile-First Interface", desc: "Bottom nav, pinch-zoom, built to actually play from your phone at the table." },
    ],
  },
  {
    id: "raiding-parties",
    tag: "Multi-Table Raids",
    name: "Scale Play Beyond A Single Table.",
    accent: "#D90429",
    bg: "wood",
    icon: Swords,
    cards: [
      { icon: Swords, title: "5 Distinct Raid Modes", desc: "From co-op alliances to a whole raid vs. one colossal boss — pick your table's flavor of chaos below.", hero: true },
      { icon: Compass, title: "Master GM + Branches", desc: "One shared world, with every table's own Branch feeding back into it." },
    ],
  },
  {
    id: "game-shows",
    tag: "Game Show Arsenal",
    name: "Keep Downtime & Taverns Unforgettable.",
    accent: "#22C55E",
    bg: "dark",
    icon: PartyPopper,
    cards: [
      { icon: PartyPopper, title: "Wheel Spinner", desc: "A spin-the-wheel minigame for prizes, twists, or pure chaos.", hero: true },
      { icon: Puzzle, title: "Trivia Board", desc: "GM-authored trivia rounds, right on the shared board." },
      { icon: Trophy, title: "Item Collection", desc: "Scavenger-hunt style collection rounds for the whole table." },
      { icon: Eye, title: "Spectator Mode", desc: "Friends can watch the show live without joining as a contestant." },
    ],
  },
  {
    id: "story-lore",
    tag: "Story & Lore",
    name: "Chronicle Every Victory & Secret.",
    accent: "#D4AF37",
    bg: "wood",
    icon: BookOpen,
    cards: [
      { icon: BookOpen, title: "Story Book", desc: "A persistent campaign journal with chapters that grow with your table.", hero: true },
      { icon: Newspaper, title: "Lore Book", desc: "Unlockable NPC backstory cards your players discover as they play." },
      { icon: History, title: "Automated Battle Log", desc: "Every fight's roll history, kept for you — no note-taking required." },
    ],
  },
  {
    id: "knights-fee",
    tag: "Knights Fee",
    name: "The Social Hearth for Adventurers.",
    accent: "#D4AF37",
    bg: "dark",
    icon: Users,
    cards: [
      { icon: Rss, title: "Player Profiles & Feed", desc: "A home base for every adventurer, with posts and a following feed.", hero: true },
      { icon: UserPlus, title: "Looking-for-Group Board", desc: "Find your next table — or fill the last seat at yours." },
      { icon: Crown, title: "RSVP to Events", desc: "Announce and join Raids, Games, and Game Shows in one tap." },
      { icon: Award, title: "Gauntlets & Rank Badges", desc: "Earn points and climb ranks across every raid you take part in." },
    ],
  },
  {
    id: "companion-bazaar",
    tag: "Emberbeard & The Bazaar",
    name: "AI Companionship & A Marketplace Just For Adventurers.",
    accent: "#D90429",
    bg: "wood",
    icon: Flame,
    cards: [
      { icon: Flame, title: "Sir Emberbeard", desc: "An AI dragon companion with real personality — and a memory of your adventures.", hero: true },
      { icon: Rss, title: "Shareable Highlights", desc: "Share Emberbeard's dramatized retellings straight to your Knights Fee feed." },
      { icon: Store, title: "The Bazaar", desc: "Buy and sell maps, tokens, NPCs, campaigns, music & SFX with other players." },
      { icon: Coins, title: "Real Seller Payouts", desc: "Stripe-powered payouts land in sellers' pockets, tracked on their own dashboard." },
    ],
  },
];

function FeatureCard({ icon: Icon, title, desc, accent, hero, testId }) {
  return (
    <div
      data-testid={testId}
      className={`group relative p-6 rounded-lg panel-wood transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_10px_30px_rgba(212,175,55,0.15)] ${
        hero ? "lg:col-span-2 flex flex-col md:flex-row md:items-center gap-5" : ""
      }`}
      style={{ borderColor: hero ? `${accent}55` : undefined }}
    >
      <div
        className="w-11 h-11 rounded-full flex items-center justify-center shrink-0 mb-4 md:mb-0 transition-colors duration-300"
        style={{ background: `${accent}1a`, border: `1px solid ${accent}55` }}
      >
        <Icon className="w-5 h-5" style={{ color: accent }} />
      </div>
      <div>
        <h4 className={`font-display ${hero ? "text-xl md:text-2xl" : "text-lg"} mb-2 text-[#f1e0c5]`}>{title}</h4>
        <p className="text-sm text-[#f1e0c5]/60 leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

function RaidModeChip({ mode }) {
  const Icon = mode.icon;
  return (
    <div
      data-testid={`landing-raid-mode-${mode.name.toLowerCase().replace(/\s+/g, "-")}`}
      className="flex-1 min-w-[150px] p-5 rounded-lg bg-[#1a1008]/60 border transition-all duration-300 hover:-translate-y-1"
      style={{ borderColor: `${mode.color}40` }}
    >
      <Icon className="w-6 h-6 mb-3" style={{ color: mode.color }} />
      <p className="font-display text-base mb-1.5" style={{ color: mode.color }}>{mode.name}</p>
      <p className="text-xs text-[#f1e0c5]/55 leading-relaxed">{mode.desc}</p>
    </div>
  );
}

function ClusterSection({ cluster }) {
  const HeaderIcon = cluster.icon;
  const bgClass = cluster.bg === "wood" ? "bg-[#1a1008]" : "bg-[#151613]";
  return (
    <section id={cluster.id} data-testid={`landing-cluster-${cluster.id}`} className={`${bgClass} px-6 md:px-12 py-16 md:py-20 border-t border-[#4a3424]/40 scroll-mt-6`}>
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: `${cluster.accent}1a`, border: `1px solid ${cluster.accent}55` }}>
            <HeaderIcon className="w-4 h-4" style={{ color: cluster.accent }} />
          </div>
          <p className="uppercase text-xs tracking-[0.25em]" style={{ color: cluster.accent }}>{cluster.tag}</p>
        </div>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl mb-8 md:mb-10 max-w-2xl text-[#f1e0c5]">{cluster.name}</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {cluster.cards.map((c, i) => (
            <FeatureCard key={c.title} icon={c.icon} title={c.title} desc={c.desc} accent={cluster.accent} hero={c.hero} testId={`landing-card-${cluster.id}-${i}`} />
          ))}
        </div>

        {cluster.id === "raiding-parties" && (
          <div data-testid="landing-raid-modes-row" className="flex flex-wrap gap-4 mt-6">
            {RAID_MODES.map((m) => (
              <RaidModeChip key={m.name} mode={m} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#151613] text-[#f1e0c5] overflow-x-hidden">
      <nav className="flex items-center justify-between px-6 md:px-12 py-6 relative z-10 border-t-4 border-[#3B82F6] shadow-[0_-4px_20px_rgba(59,130,246,0.15)]">
        <div className="font-display text-2xl tracking-wide flex items-center gap-2.5" data-testid="landing-logo">
          <img src="/logo192.png" alt="Call to Arms" className="w-8 h-8 rounded-md shadow-[0_0_12px_rgba(217,4,41,0.35)]" data-testid="brand-logo-icon" />
          Call to Arms<span className="text-[#D90429]">.</span>
        </div>
        <div className="flex gap-3">
          <Link to="/login" data-testid="landing-login-link" className="px-4 py-2 text-sm uppercase tracking-widest text-[#f1e0c5]/80 hover:text-[#D4AF37] transition-colors duration-200">
            Log In
          </Link>
          <Link
            to="/register"
            data-testid="landing-raid-cta-button"
            className="px-5 py-2 rounded-full border border-[#3B82F6]/60 text-[#3B82F6] text-sm font-semibold uppercase tracking-widest hover:bg-[#3B82F6]/10 transition-colors duration-200 flex items-center gap-1.5"
          >
            <Swords className="w-3.5 h-3.5" /> Start a Raid
          </Link>
          <Link
            to="/register"
            data-testid="landing-gm-cta-button"
            className="px-5 py-2 rounded-full bg-[#1E3A8A] text-[#F1E0C5] text-sm font-semibold uppercase tracking-widest hover:bg-[#2b4fab] transition-colors duration-200"
          >
            Start a Game
          </Link>
        </div>
      </nav>

      <section className="relative px-6 md:px-12 pt-16 pb-24 md:pt-24 md:pb-28">
        <div
          className="absolute inset-0 bg-grain pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(180deg, rgba(21,22,19,0.35), rgba(21,22,19,0.95)), url('https://images.pexels.com/photos/2873895/pexels-photo-2873895.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1800')",
            backgroundSize: "cover",
            backgroundPosition: "center 78%",
          }}
        />
        <div className="relative max-w-3xl">
          <p className="uppercase text-xs tracking-[0.3em] text-[#D4AF37] mb-6">Pathfinder 2e &amp; D&amp;D 5e · Virtual Tabletop</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight mb-8">
            Call to Arms.<br />Do you answer?
          </h1>
          <p className="text-base md:text-lg text-[#f1e0c5]/70 max-w-xl mb-8 leading-relaxed">
            A friendly, drag-and-drop VTT built Pathfinder 2e-first, now welcoming D&amp;D 5e tables too. Preloaded SRD rules
            automate every modifier, passive ability and proficiency — so your table can just play.
          </p>
          <div className="flex flex-wrap gap-2.5 mb-10" data-testid="landing-quick-badges">
            {CLUSTERS.map((cluster) => (
              <button
                key={cluster.id}
                type="button"
                onClick={() => scrollToCluster(cluster.id)}
                data-testid={`landing-badge-${cluster.id}`}
                className="px-3.5 py-1.5 rounded-full border border-[#D4AF37]/35 bg-[#151613]/60 backdrop-blur-md text-xs uppercase tracking-wider text-[#D4AF37]/90 hover:bg-[#D4AF37]/15 hover:border-[#D4AF37]/70 hover:text-[#D4AF37] transition-colors duration-200 cursor-pointer"
              >
                {cluster.tag}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/register"
              data-testid="landing-hero-create-button"
              className="px-8 py-4 rounded-full bg-[#1E3A8A] text-[#F1E0C5] font-semibold uppercase tracking-widest text-sm hover:bg-[#2b4fab] transition-colors duration-200 shadow-[0_0_30px_rgba(30,58,138,0.4)]"
            >
              Create a Game Room
            </Link>
            <Link
              to="/login"
              data-testid="landing-hero-join-button"
              className="px-8 py-4 rounded-full border border-[#f1e0c5]/20 text-[#f1e0c5] font-semibold uppercase tracking-widest text-sm hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-200"
            >
              Join With Invite Link
            </Link>
          </div>
        </div>
      </section>

      {CLUSTERS.map((cluster) => (
        <ClusterSection key={cluster.id} cluster={cluster} />
      ))}

      <section className="px-6 md:px-12 py-20 bg-[#1E3A8A] relative overflow-hidden">
        <div className="absolute inset-0 bg-grain pointer-events-none opacity-40" />
        <div className="relative max-w-3xl mx-auto text-center">
          <h2 className="font-display text-3xl sm:text-4xl mb-4 text-[#f1e0c5]">Every one of these. One table. Zero setup.</h2>
          <p className="text-base text-[#f1e0c5]/75 mb-8 max-w-xl mx-auto leading-relaxed">
            Your first game room is free to create — bring your table and see which feature they fall in love with first.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/register"
              data-testid="landing-final-cta-button"
              className="px-8 py-4 rounded-full bg-[#D4AF37] text-[#1a1008] font-semibold uppercase tracking-widest text-sm hover:bg-[#e0bd4d] transition-colors duration-200 shadow-[0_0_30px_rgba(212,175,55,0.35)]"
            >
              Create a Game Room
            </Link>
            <Link
              to="/register"
              data-testid="landing-final-raid-button"
              className="px-8 py-4 rounded-full border border-[#f1e0c5]/30 text-[#f1e0c5] font-semibold uppercase tracking-widest text-sm hover:border-[#f1e0c5] transition-colors duration-200 flex items-center gap-2"
            >
              <Swords className="w-4 h-4" /> Start a Raid
            </Link>
          </div>
        </div>
      </section>

      <footer className="px-6 md:px-12 py-10 text-center text-xs uppercase tracking-[0.2em] text-[#f1e0c5]/40">
        Call to Arms — built for the table, not the sales floor.
      </footer>
    </div>
  );
}
